import { useState } from 'react';
import type { DifficultyRating, FeelingRating, SorenessRating, FeedbackData } from '../types';

interface FeedbackPhaseProps {
  onSubmit: (feedback: FeedbackData) => Promise<void>;
  onSkip: () => void;
}

const difficultyOptions: { value: DifficultyRating; label: string; emoji: string }[] = [
  { value: 'easy', label: 'Easy', emoji: '😊' },
  { value: 'moderate', label: 'Moderate', emoji: '💪' },
  { value: 'hard', label: 'Hard', emoji: '😤' },
  { value: 'maxed', label: 'Very Challenging', emoji: '🔥' },
];

const feelingOptions: { value: FeelingRating; label: string; emoji: string }[] = [
  { value: 'fresh', label: 'Fresh', emoji: '🌿' },
  { value: 'worked', label: 'Well Worked', emoji: '✅' },
  { value: 'drained', label: 'Drained', emoji: '😮‍💨' },
];

const sorenessOptions: { value: SorenessRating; label: string; emoji: string }[] = [
  { value: 'none', label: 'None', emoji: '👍' },
  { value: 'mild', label: 'Mild', emoji: '🤏' },
  { value: 'noticeable', label: 'Noticeable', emoji: '😬' },
  { value: 'heavy', label: 'Heavy', emoji: '🥵' },
];

export function FeedbackPhase({ onSubmit, onSkip }: FeedbackPhaseProps) {
  const [difficulty, setDifficulty] = useState<DifficultyRating | null>(null);
  const [feeling, setFeeling] = useState<FeelingRating | null>(null);
  const [soreness, setSoreness] = useState<SorenessRating | null>(null);
  const [readyToTrain, setReadyToTrain] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = difficulty !== null && feeling !== null && soreness !== null;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        overallDifficulty: difficulty,
        postSessionFeeling: feeling,
        sorenessLevel: soreness,
        readyToTrainAgain: readyToTrain,
      });
    } catch {
      setError('Failed to submit feedback. You can skip for now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-5 p-6" data-testid="feedback-form">
      <h2 className="text-heading-lg text-gray-900 dark:text-textDark-primary">
        How did that feel?
      </h2>
      <p className="text-body text-gray-500 dark:text-textDark-muted">
        Your feedback helps personalise future sessions
      </p>

      {/* Difficulty */}
      <div className="w-full max-w-lg">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-textDark-secondary">
          Overall Difficulty
        </label>
        <div className="grid grid-cols-2 gap-2" data-testid="feedback-difficulty-options">
          {difficultyOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-testid={`feedback-difficulty-${opt.value}`}
              onClick={() => setDifficulty(opt.value)}
              className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                difficulty === opt.value
                  ? 'border-brand-600 bg-brand-600 text-white ring-2 ring-brand-300 shadow-sm dark:border-brand-500 dark:bg-brand-600 dark:text-white dark:ring-brand-400'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:text-textDark-secondary dark:hover:bg-dark-elevated'
              }`}
            >
              <span className="mr-1.5">{opt.emoji}</span>{opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Feeling */}
      <div className="w-full max-w-lg">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-textDark-secondary">
          How Do You Feel?
        </label>
        <div className="grid grid-cols-3 gap-2" data-testid="feedback-feeling-options">
          {feelingOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-testid={`feedback-feeling-${opt.value}`}
              onClick={() => setFeeling(opt.value)}
              className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                feeling === opt.value
                  ? 'border-brand-600 bg-brand-600 text-white ring-2 ring-brand-300 shadow-sm dark:border-brand-500 dark:bg-brand-600 dark:text-white dark:ring-brand-400'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:text-textDark-secondary dark:hover:bg-dark-elevated'
              }`}
            >
              <span className="mr-1.5">{opt.emoji}</span>{opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Soreness */}
      <div className="w-full max-w-lg">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-textDark-secondary">
          Soreness Level
        </label>
        <div className="grid grid-cols-2 gap-2" data-testid="feedback-soreness-options">
          {sorenessOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-testid={`feedback-soreness-${opt.value}`}
              onClick={() => setSoreness(opt.value)}
              className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                soreness === opt.value
                  ? 'border-brand-600 bg-brand-600 text-white ring-2 ring-brand-300 shadow-sm dark:border-brand-500 dark:bg-brand-600 dark:text-white dark:ring-brand-400'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:text-textDark-secondary dark:hover:bg-dark-elevated'
              }`}
            >
              <span className="mr-1.5">{opt.emoji}</span>{opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ready to train again */}
      <div className="w-full max-w-lg">
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-textDark-secondary">
          Ready for another session soon?
        </label>
        <div className="grid grid-cols-2 gap-2" data-testid="feedback-ready-options">
          <button
            type="button"
            data-testid="feedback-ready-yes"
            onClick={() => setReadyToTrain(true)}
            className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
              readyToTrain
                ? 'border-brand-600 bg-brand-600 text-white ring-2 ring-brand-300 shadow-sm dark:border-brand-500 dark:bg-brand-600 dark:text-white dark:ring-brand-400'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:text-textDark-secondary dark:hover:bg-dark-elevated'
            }`}
          >
            <span className="mr-1.5">👍</span>Yes
          </button>
          <button
            type="button"
            data-testid="feedback-ready-no"
            onClick={() => setReadyToTrain(false)}
            className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
              !readyToTrain
                ? 'border-brand-600 bg-brand-600 text-white ring-2 ring-brand-300 shadow-sm dark:border-brand-500 dark:bg-brand-600 dark:text-white dark:ring-brand-400'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:text-textDark-secondary dark:hover:bg-dark-elevated'
            }`}
          >
            <span className="mr-1.5">😴</span>Not Yet
          </button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-amber-600 dark:text-amber-400" data-testid="feedback-error">
          {error}
        </p>
      )}

      {/* Actions */}
      <div className="flex w-full max-w-lg gap-3 pt-2">
        <button
          type="button"
          onClick={onSkip}
          data-testid="feedback-skip-button"
          className="flex-1 rounded-lg border border-gray-300 px-4 py-3 text-center text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-dark-border dark:text-textDark-secondary dark:hover:bg-dark-elevated"
        >
          Skip
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          data-testid="feedback-submit-button"
          className="flex-1 rounded-lg bg-brand-600 px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-brand-700 active:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting…' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );
}
